/**
 * 设置面板组件
 */
import { createAPIClient, type APIClient } from '../api/client.js';
import type { SendKeyMode } from './InputBox.js';

export interface SettingsPanelOptions {
  token: string;
  apiBaseUrl: string;
  onSendKeyModeChange?: (mode: SendKeyMode) => void;
}

export class SettingsPanel {
  private options: SettingsPanelOptions;
  private apiClient: APIClient;
  private panel: HTMLElement | null = null;
  private closeBtn: HTMLElement | null = null;
  private keysList: HTMLElement | null = null;
  private addKeyForm: HTMLFormElement | null = null;
  private sendKeyModeSelect: HTMLSelectElement | null = null;
  private grobList: HTMLElement | null = null;
  private grobStatus: HTMLElement | null = null;
  private addGrobForm: HTMLFormElement | null = null;
  private initialized = false;

  constructor(options: SettingsPanelOptions) {
    this.options = options;
    this.apiClient = createAPIClient(options.apiBaseUrl);
    this.apiClient.setToken(options.token);
  }

  /**
   * 初始化组件
   */
  private init(): void {
    // 防止重复初始化
    if (this.initialized) return;
    this.initialized = true;
    this.panel = document.getElementById('settings-panel');
    this.closeBtn = document.getElementById('close-settings');
    this.keysList = document.getElementById('keys-list');
    this.addKeyForm = document.getElementById('add-key-form') as HTMLFormElement;
    this.sendKeyModeSelect = document.getElementById('send-key-mode') as HTMLSelectElement;
    this.grobList = document.getElementById('grob-list');
    this.grobStatus = document.getElementById('grob-status');
    this.addGrobForm = document.getElementById('add-grob-form') as HTMLFormElement;

    // 绑定关闭按钮
    this.closeBtn?.addEventListener('click', () => this.hide());

    // 绑定修改密码表单
    const changePasswordForm = document.getElementById('change-password-form') as HTMLFormElement;
    changePasswordForm?.addEventListener('submit', (e) => this.handleChangePassword(e));

    // 绑定添加 Key 表单
    this.addKeyForm?.addEventListener('submit', (e) => this.handleFormSubmit(e));

    // 绑定发送快捷键设置
    this.sendKeyModeSelect?.addEventListener('change', (e) => this.handleSendKeyModeChange(e));

    // 使用事件委托处理 Keys 列表按钮
    this.keysList?.addEventListener('click', (e) => this.handleKeysListClick(e));

    // 绑定 Grob 表单
    this.addGrobForm?.addEventListener('submit', (e) => this.handleGrobFormSubmit(e));

    // 绑定 Grob 重启按钮
    const restartGrobBtn = document.getElementById('restart-grob-btn');
    restartGrobBtn?.addEventListener('click', () => this.handleRestartGrob());

    // 绑定 Grob 取消按钮
    const cancelGrobBtn = document.getElementById('grob-form-cancel-btn');
    cancelGrobBtn?.addEventListener('click', () => this.resetGrobForm());

    // 使用事件委托处理 Grob 列表按钮
    this.grobList?.addEventListener('click', (e) => this.handleGrobListClick(e));

  }

  /**
   * 处理发送快捷键设置变更
   */
  private handleSendKeyModeChange(e: Event): void {
    const select = e.target as HTMLSelectElement;
    const mode = select.value as SendKeyMode;
    this.options.onSendKeyModeChange?.(mode);
  }

  /**
   * 设置发送快捷键模式
   */
  setSendKeyMode(mode: SendKeyMode): void {
    if (this.sendKeyModeSelect) {
      this.sendKeyModeSelect.value = mode;
    }
  }

  /**
   * 处理修改密码
   */
  private async handleChangePassword(e: Event): Promise<void> {
    e.preventDefault();

    const currentPassword = (document.getElementById('current-password') as HTMLInputElement)?.value;
    const newPassword = (document.getElementById('new-password') as HTMLInputElement)?.value;
    const confirmPassword = (document.getElementById('confirm-password') as HTMLInputElement)?.value;

    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('请填写所有字段');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('新密码和确认密码不一致');
      return;
    }

    if (newPassword.length < 8) {
      alert('新密码长度至少为 8 位');
      return;
    }

    try {
      await this.apiClient.changePassword({ currentPassword, newPassword });
      alert('密码修改成功');

      // 清空表单
      const form = e.target as HTMLFormElement;
      form.reset();
    } catch (error) {
      console.error('Failed to change password:', error);
      alert(error instanceof Error ? error.message : '修改密码失败');
    }
  }

  /**
   * 处理 Keys 列表点击
   */
  private async handleKeysListClick(e: Event): Promise<void> {
    const target = e.target as HTMLElement;

    // 检查是否点击了整个 Key 卡片（用于选择/取消选择）
    const keyItem = target.closest('.key-item') as HTMLElement;
    if (keyItem && !target.closest('button')) {
      // 点击的是卡片本身（不是按钮），执行选择/取消
      const keyId = keyItem.dataset.keyId;
      if (keyId) {
        const isSelected = keyItem.classList.contains('selected');
        await this.handleToggleSelection(keyId, isSelected);
      }
      return;
    }

    // 检查是否点击了按钮
    const button = target.closest('button[data-action]') as HTMLButtonElement;
    if (!button) return;

    const action = button.dataset.action;
    const keyId = keyItem?.dataset.keyId;

    if (!keyId) return;

    console.log('Button clicked:', { action, keyId });

    switch (action) {
      case 'edit':
        await this.handleEditKey(button);
        break;
      case 'toggle':
        await this.handleToggleKey(keyId, button.dataset.enabled === 'true');
        break;
      case 'reset':
        await this.handleResetKey(keyId);
        break;
      case 'delete':
        await this.handleDeleteKey(keyId);
        break;
      case 'set-limit':
        await this.handleSetDailyLimit(keyId);
        break;
      case 'reset-usage':
        await this.handleResetUsage(keyId);
        break;
    }
  }

  /**
   * 处理单击切换选择状态（单击选中，再次单击取消）
   */
  private async handleToggleSelection(keyId: string, currentlySelected: boolean): Promise<void> {
    try {
      if (currentlySelected) {
        // 当前已选中，执行取消选择（切换为自动模式）
        await this.apiClient.clearSelection();
        this.showNotification('已切换到自动模式');
      } else {
        // 当前未选中，执行选择
        try {
          await this.apiClient.selectKey(keyId);
          this.showNotification('已选择此 API Key');
        } catch (error) {
          // 检查是否是容器忙碌错误 (409)
          const errorMsg = error instanceof Error ? error.message : '';
          if (errorMsg.includes('container_busy') || errorMsg.includes('409')) {
            // 显示确认对话框
            const confirmed = confirm(
              '当前有消息正在处理中，切换模型将中断当前处理。\n\n是否确认强制切换？\n（强制切换可能会丢失当前正在处理的回复）',
            );
            if (!confirmed) {
              return; // 用户取消
            }
            // 用户确认强制切换
            await this.apiClient.selectKeyForce(keyId);
            this.showNotification('已强制切换 API Key，容器将重启');
          } else {
            throw error; // 其他错误继续抛出
          }
        }
      }
      await this.loadKeys();
    } catch (error) {
      console.error('Failed to toggle selection:', error);
      const errorMsg = error instanceof Error ? error.message : '操作失败';
      this.showNotification(`错误: ${errorMsg}`);
    }
  }

  /**
   * 处理设置每日限额
   */
  private async handleSetDailyLimit(keyId: string): Promise<void> {
    const limit = prompt('请输入每日限额（0表示不限额）：');
    if (limit === null) return;

    const limitNum = parseInt(limit, 10);
    if (isNaN(limitNum) || limitNum < 0) {
      alert('请输入有效的非负整数');
      return;
    }

    try {
      await this.apiClient.setDailyLimit(keyId, limitNum);
      await this.loadKeys();
      this.showNotification(`每日限额已设置为 ${limitNum === 0 ? '不限额' : limitNum}`);
    } catch (error) {
      console.error('Failed to set daily limit:', error);
      alert(error instanceof Error ? error.message : '设置失败');
    }
  }

  /**
   * 处理重置使用量
   */
  private async handleResetUsage(keyId: string): Promise<void> {
    if (!confirm('确定要重置此 API Key 的每日使用量吗？')) return;

    try {
      await this.apiClient.resetUsage(keyId);
      await this.loadKeys();
      this.showNotification('每日使用量已重置');
    } catch (error) {
      console.error('Failed to reset usage:', error);
      alert(error instanceof Error ? error.message : '重置失败');
    }
  }

  /**
   * 处理编辑 API Key - 使用表单
   */
  private async handleEditKey(button: HTMLButtonElement): Promise<void> {
    const keyId = button.dataset.keyId || '';
    const currentModel = button.dataset.model || '';
    const currentBaseUrl = button.dataset.baseurl || '';
    const currentPriority = button.dataset.priority || '0';

    // 切换到编辑模式
    this.setEditMode(keyId, currentModel, currentBaseUrl, currentPriority);
  }

  /**
   * 设置表单为编辑模式
   */
  private setEditMode(keyId: string, model: string, baseUrl: string, priority: string): void {
    const formTitle = document.querySelector('#add-key-form h4');
    const submitBtn = document.getElementById('form-submit-btn');
    const cancelBtn = document.getElementById('form-cancel-btn');
    const apiKeyInput = document.getElementById('new-api-key') as HTMLInputElement;
    const baseUrlInput = document.getElementById('new-base-url') as HTMLInputElement;
    const modelInput = document.getElementById('new-model') as HTMLInputElement;
    const priorityInput = document.getElementById('new-priority') as HTMLInputElement;
    const editKeyIdInput = document.getElementById('edit-key-id') as HTMLInputElement;

    if (!formTitle || !submitBtn || !cancelBtn || !apiKeyInput) return;

    // 修改表单为编辑模式
    formTitle.textContent = '编辑 Key';
    submitBtn.textContent = '保存';
    cancelBtn.style.display = 'inline-block';

    // 填充当前值（API Key 留空）
    apiKeyInput.value = '';
    apiKeyInput.placeholder = '留空表示不修改 API Key';
    apiKeyInput.required = false;
    baseUrlInput.value = baseUrl;
    modelInput.value = model;
    priorityInput.value = priority;
    editKeyIdInput.value = keyId;

    // 滚动到表单
    document.getElementById('add-key-form')?.scrollIntoView({ behavior: 'smooth' });

    // 绑定取消按钮（一次性）
    cancelBtn.onclick = () => this.resetForm();
  }

  /**
   * 重置表单为添加模式
   */
  private resetForm(): void {
    const formTitle = document.querySelector('#add-key-form h4');
    const submitBtn = document.getElementById('form-submit-btn');
    const cancelBtn = document.getElementById('form-cancel-btn');
    const apiKeyInput = document.getElementById('new-api-key') as HTMLInputElement;
    const baseUrlInput = document.getElementById('new-base-url') as HTMLInputElement;
    const modelInput = document.getElementById('new-model') as HTMLInputElement;
    const priorityInput = document.getElementById('new-priority') as HTMLInputElement;
    const editKeyIdInput = document.getElementById('edit-key-id') as HTMLInputElement;
    const form = document.getElementById('add-key-form') as HTMLFormElement;

    if (!formTitle || !submitBtn || !cancelBtn) return;

    // 恢复添加模式
    formTitle.textContent = '添加新 Key';
    submitBtn.textContent = '添加';
    cancelBtn.style.display = 'none';

    // 重置字段
    apiKeyInput.value = '';
    apiKeyInput.placeholder = 'sk-ant-...';
    apiKeyInput.required = true;
    baseUrlInput.value = 'https://api.anthropic.com';
    modelInput.value = 'claude-sonnet-4-20250514';
    priorityInput.value = '0';
    editKeyIdInput.value = '';

    // 重置表单
    form.reset();
  }

  /**
   * 处理表单提交（添加或编辑）
   */
  private async handleFormSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const editKeyId = (document.getElementById('edit-key-id') as HTMLInputElement)?.value;
    const apiKey = (document.getElementById('new-api-key') as HTMLInputElement)?.value;
    const baseUrl = (document.getElementById('new-base-url') as HTMLInputElement)?.value || 'https://api.anthropic.com';
    const model = (document.getElementById('new-model') as HTMLInputElement)?.value || 'claude-sonnet-4-20250514';
    const priority = parseInt((document.getElementById('new-priority') as HTMLInputElement)?.value || '0', 10);

    try {
      if (editKeyId) {
        // 编辑模式
        const updates: { apiKey?: string; baseUrl: string; model: string; priority: number } = {
          baseUrl,
          model,
          priority,
        };
        // 如果填写了新 API Key，则更新
        if (apiKey) {
          updates.apiKey = apiKey;
        }

        await this.apiClient.updateKey(editKeyId, updates);
        this.showNotification('API Key 已更新');
      } else {
        // 添加模式
        if (!apiKey) {
          alert('请输入 API Key');
          return;
        }
        await this.apiClient.addKey({ apiKey, baseUrl, model, priority });
        this.showNotification('API Key 已添加');
      }

      await this.loadKeys();
      this.resetForm();
    } catch (error) {
      console.error('Failed to save key:', error);
      alert(error instanceof Error ? error.message : '操作失败');
    }
  }

  /**
   * 处理切换 Key 状态
   */
  private async handleToggleKey(keyId: string, shouldEnable: boolean): Promise<void> {
    const buttonText = shouldEnable ? '启用' : '禁用';
    try {
      await this.apiClient.toggleKey(keyId, shouldEnable);
      await this.loadKeys();
      // 显示成功反馈
      this.showNotification(`API Key 已${buttonText}`);
    } catch (error) {
      console.error('Failed to toggle key:', error);
      alert(error instanceof Error ? error.message : '操作失败');
    }
  }

  /**
   * 显示通知
   */
  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--color-success, #22c55e);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      animation: fadeIn 0.3s ease;
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }

  /**
   * 处理重置 Key 错误
   */
  private async handleResetKey(keyId: string): Promise<void> {
    try {
      await this.apiClient.resetKey(keyId);
      await this.loadKeys();
      this.showNotification('API Key 错误计数已重置');
    } catch (error) {
      console.error('Failed to reset key:', error);
      alert(error instanceof Error ? error.message : '重置失败');
    }
  }

  /**
   * 处理删除 Key
   */
  private async handleDeleteKey(keyId: string): Promise<void> {
    if (!confirm('确定要删除这个 API Key 吗？')) return;

    try {
      await this.apiClient.deleteKey(keyId);
      await this.loadKeys();
      this.showNotification('API Key 已删除');
    } catch (error) {
      console.error('Failed to delete key:', error);
      alert(error instanceof Error ? error.message : '删除失败');
    }
  }

  /**
   * 加载 API Keys
   */
  private async loadKeys(): Promise<void> {
    try {
      const response = await this.apiClient.getKeys();

      if (!this.keysList) return;

      if (!response.keys || response.keys.length === 0) {
        this.keysList.innerHTML = '<p style="color: var(--color-text-muted); text-align: center;">暂无 API Key</p>';
        return;
      }

      this.keysList.innerHTML = '';

      // 添加选择模式提示
      const modeHint = document.createElement('div');
      modeHint.className = 'selection-mode-hint';
      modeHint.style.cssText = `
        padding: 8px;
        margin-bottom: 10px;
        background: var(--color-bg-secondary, #f5f5f5);
        border-radius: 6px;
        text-align: center;
        font-size: 12px;
        color: var(--color-text-muted);
      `;
      modeHint.innerHTML = `💡 单击卡片选择/取消选中，选中后优先使用`;
      this.keysList.appendChild(modeHint);

      response.keys.forEach((key) => {
        const keyDiv = document.createElement('div');
        keyDiv.className = `key-item ${key.disabled ? 'disabled' : ''} ${key.isSelected ? 'selected' : ''}`;
        keyDiv.dataset.keyId = key.id;

        // 添加光标样式，表示可点击
        keyDiv.style.cursor = 'pointer';

        // 计算每日限额状态
        const dailyLimit = key.dailyLimit || 0;
        const dailyUsed = key.dailyUsed || 0;
        const isAtLimit = dailyLimit > 0 && dailyUsed >= dailyLimit;
        const limitPercent = dailyLimit > 0 ? Math.round((dailyUsed / dailyLimit) * 100) : 0;

        keyDiv.innerHTML = `
          <div class="key-header">
            <span class="key-id">
              ${key.isSelected ? '✓ ' : ''}${key.model}
            </span>
            <div class="key-actions">
              <button class="edit" data-action="edit" data-key-id="${key.id}" data-model="${key.model}" data-baseurl="${key.baseUrl}" data-priority="${key.priority}">改</button>
              <button class="toggle ${key.disabled ? 'off' : 'on'}" data-action="toggle" data-enabled="${key.disabled}">
                ${key.disabled ? '开' : '关'}
              </button>
              <button class="set-limit" data-action="set-limit">额</button>
              <button class="reset" data-action="reset">清</button>
              <button class="delete" data-action="delete">删</button>
            </div>
          </div>
          <div class="key-info">
            <span style="color: var(--color-text-primary, #333); font-weight: 500;">${key.baseUrl}</span>
            ${key.isSelected ? '<span class="selected-badge">当前选中</span>' : ''}
            ${key.errorCount > 0 ? `<span style="color: var(--color-error);">⚠ ${key.errorCount} 错误</span>` : ''}
            <span>优先: ${key.priority}</span>
            <span>请求: ${key.requestCount || 0}</span>
            ${dailyLimit > 0 ? `<span>${dailyUsed}/${dailyLimit}</span>` : ''}
          </div>
        `;

        this.keysList!.appendChild(keyDiv);
      });
    } catch (error) {
      console.error('Failed to load keys:', error);
      // 显示详细错误信息
      if (this.keysList) {
        this.keysList.innerHTML = `
          <div style="padding: 20px; text-align: center; color: var(--color-error);">
            <p>加载 API Key 失败</p>
            <p style="font-size: 12px; color: var(--color-text-muted);">${error instanceof Error ? error.message : '未知错误'}</p>
            <button onclick="location.reload()" style="margin-top: 10px; padding: 8px 16px;">重新加载</button>
          </div>
        `;
      }
    }
  }

  /**
   * 显示设置面板
   */
  show(): void {
    this.init();

    if (this.panel) {
      this.panel.style.display = 'flex';
    }

    // 重置表单为添加模式
    this.resetForm();
    this.resetGrobForm();

    // 加载数据
    this.loadKeys();
    this.loadGrob();
  }

  /**
   * 处理面板外点击（用于关闭面板）
   */
  private handleOutsideClick = (e: MouseEvent): void => {
    const target = e.target as Node;
    // 检查是否点击了设置按钮（避免点击设置按钮时立即关闭）
    const settingsBtn = document.getElementById('settings-btn');
    if (target === settingsBtn || (target as HTMLElement)?.closest?.('#settings-btn')) {
      return; // 点击的是设置按钮，不关闭
    }

    if (this.panel && !this.panel.contains(target)) {
      // 点击在面板外部，关闭面板
      this.hide();
    }
  }

  /**
   * 显示设置面板（带点击外部关闭功能）
   */
  showWithOutsideClick(): void {
    this.show();
    // 添加点击外部关闭的事件监听
    document.addEventListener('click', this.handleOutsideClick);
  }

  /**
   * 隐藏设置面板
   */
  hide(): void {
    // 移除点击外部关闭的事件监听
    document.removeEventListener('click', this.handleOutsideClick);

    if (this.panel) {
      this.panel.style.display = 'none';
    }
  }

  // ========== Grob API 代理管理 ==========

  /**
   * 加载 Grob 状态和配置
   */
  private async loadGrob(): Promise<void> {
    try {
      // 获取状态
      const status = await this.apiClient.getGrobStatus();

      if (this.grobStatus) {
        const statusHtml = `
          <div class="grob-status-bar" style="display: flex; gap: 15px; padding: 10px; background: var(--color-bg-secondary, #f5f5f5); border-radius: 6px; margin-bottom: 10px; flex-wrap: wrap;">
            <div><span style="color: var(--color-text-muted);">状态:</span> <span style="font-weight: 500; color: ${status.running ? 'var(--color-success, #22c55e)' : 'var(--color-error, #dc3545)'};">${status.running ? '运行中' : '已停止'}</span></div>
            <div><span style="color: var(--color-text-muted);">端口:</span> ${status.port}</div>
            <div><span style="color: var(--color-text-muted);">实际模型:</span> <span style="font-weight: 500; color: var(--color-primary, #007bff);">${status.currentModel || '-'}</span></div>
            <div><span style="color: var(--color-text-muted);">Providers:</span> ${status.providersCount}</div>
            ${status.spend !== undefined ? `<div><span style="color: var(--color-text-muted);">花费:</span> $${status.spend.toFixed(6)}</div>` : ''}
          </div>
        `;
        this.grobStatus.innerHTML = statusHtml;
      }

      // 获取配置
      const config = await this.apiClient.getGrobConfig();

      if (!this.grobList) return;

      if (!config.providers || config.providers.length === 0) {
        this.grobList.innerHTML = '<p style="color: var(--color-text-muted); text-align: center;">暂无 Grob API 配置</p>';
        return;
      }

      this.grobList.innerHTML = '';

      config.providers.forEach((provider) => {
        // 查找对应的模型映射
        const modelConfig = config.models.find((m) => m.name === 'claude-sonnet');
        const mapping = modelConfig?.mappings.find((m) => m.provider === provider.name);

        const div = document.createElement('div');
        div.className = 'key-item';
        div.innerHTML = `
          <div class="key-header">
            <span class="key-id">${provider.name}</span>
            <div class="key-actions">
              <button class="edit" data-action="edit-grob" data-name="${provider.name}" data-baseurl="${provider.base_url}" data-model="${mapping?.actual_model || provider.models?.[0] || ''}" data-priority="${mapping?.priority || 1}">改</button>
              <button class="delete" data-action="delete-grob" data-name="${provider.name}">删</button>
            </div>
          </div>
          <div class="key-info">
            <span style="color: var(--color-text-primary, #333); font-weight: 500;">${provider.base_url}</span>
            <span>模型: ${mapping?.actual_model || provider.models?.[0] || '-'}</span>
            <span>Key: ${provider.api_key}</span>
            <span>优先级: ${mapping?.priority || '-'}</span>
          </div>
        `;
        this.grobList!.appendChild(div);
      });
    } catch (error) {
      console.error('Failed to load Grob:', error);
      if (this.grobStatus) {
        this.grobStatus.innerHTML = `<div style="color: var(--color-error); padding: 10px;">加载 Grob 状态失败: ${error instanceof Error ? error.message : '未知错误'}</div>`;
      }
    }
  }

  /**
   * 处理 Grob 表单提交
   */
  private async handleGrobFormSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const editName = (document.getElementById('edit-grob-name') as HTMLInputElement)?.value;
    const name = (document.getElementById('grob-name') as HTMLInputElement)?.value;
    const baseUrl = (document.getElementById('grob-base-url') as HTMLInputElement)?.value;
    const apiKey = (document.getElementById('grob-api-key') as HTMLInputElement)?.value;
    const model = (document.getElementById('grob-model') as HTMLInputElement)?.value;
    const priority = parseInt((document.getElementById('grob-priority') as HTMLInputElement)?.value || '1', 10);

    if (!name || !baseUrl || !model) {
      alert('请填写必要字段');
      return;
    }

    try {
      if (editName) {
        // 编辑模式：更新 provider
        const updateData: { base_url?: string; api_key?: string; model?: string; priority?: number } = {
          base_url: baseUrl,
          model,
          priority,
        };
        if (apiKey) {
          updateData.api_key = apiKey;
        }

        await this.apiClient.updateGrobProvider(editName, updateData);
        this.showNotification('Grob API 已更新');

        // 重置表单
        this.resetGrobForm();
      } else {
        // 添加模式
        if (!apiKey) {
          alert('请填写 API Key');
          return;
        }

        await this.apiClient.addGrobProvider({
          name,
          provider_type: 'anthropic',
          base_url: baseUrl,
          api_key: apiKey,
          model,
          priority,
        });

        this.showNotification('Grob API 已添加');

        // 重置表单
        const form = document.getElementById('add-grob-form') as HTMLFormElement;
        form?.reset();
      }

      // 重新加载
      await this.loadGrob();
    } catch (error) {
      console.error('Failed to save Grob provider:', error);
      alert(error instanceof Error ? error.message : '操作失败');
    }
  }

  /**
   * 处理 Grob 列表点击
   */
  private async handleGrobListClick(e: Event): Promise<void> {
    const target = e.target as HTMLElement;
    const button = target.closest('button[data-action]') as HTMLButtonElement;
    if (!button) return;

    const action = button.dataset.action;
    const name = button.dataset.name;

    if (!name) return;

    if (action === 'edit-grob') {
      // 进入编辑模式
      this.setGrobEditMode(
        name,
        button.dataset.baseurl || '',
        button.dataset.model || '',
        button.dataset.priority || '1'
      );
    } else if (action === 'delete-grob') {
      if (!confirm(`确定要删除 Grob API: ${name} 吗？`)) return;

      try {
        await this.apiClient.deleteGrobProvider(name);
        this.showNotification('Grob API 已删除');
        await this.loadGrob();
      } catch (error) {
        console.error('Failed to delete Grob provider:', error);
        alert(error instanceof Error ? error.message : '删除失败');
      }
    }
  }

  /**
   * 设置 Grob 表单为编辑模式
   */
  private setGrobEditMode(name: string, baseUrl: string, model: string, priority: string): void {
    const formTitle = document.querySelector('#add-grob-form h4') as HTMLElement;
    const submitBtn = document.getElementById('grob-form-submit-btn') as HTMLButtonElement;
    const cancelBtn = document.getElementById('grob-form-cancel-btn') as HTMLButtonElement;
    const nameInput = document.getElementById('grob-name') as HTMLInputElement;
    const baseUrlInput = document.getElementById('grob-base-url') as HTMLInputElement;
    const apiKeyInput = document.getElementById('grob-api-key') as HTMLInputElement;
    const modelInput = document.getElementById('grob-model') as HTMLInputElement;
    const priorityInput = document.getElementById('grob-priority') as HTMLInputElement;
    const editNameInput = document.getElementById('edit-grob-name') as HTMLInputElement;

    if (!formTitle || !submitBtn || !cancelBtn || !nameInput) return;

    // 修改表单为编辑模式
    formTitle.textContent = '编辑 API';
    submitBtn.textContent = '保存';
    cancelBtn.style.display = 'inline-block';

    // 填充当前值（API Key 留空）
    nameInput.value = name;
    nameInput.disabled = true; // 名称不可修改
    baseUrlInput.value = baseUrl;
    apiKeyInput.value = '';
    apiKeyInput.placeholder = '留空表示不修改 API Key';
    apiKeyInput.required = false; // 编辑模式下 API Key 可选
    modelInput.value = model;
    priorityInput.value = priority;

    // 添加隐藏字段保存原始名称
    if (!editNameInput) {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.id = 'edit-grob-name';
      hiddenInput.name = 'edit-name';
      document.getElementById('add-grob-form')?.appendChild(hiddenInput);
    }
    const hiddenEditName = document.getElementById('edit-grob-name') as HTMLInputElement;
    if (hiddenEditName) hiddenEditName.value = name;

    // 滚动到表单
    document.getElementById('add-grob-form')?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * 重置 Grob 表单为添加模式
   */
  private resetGrobForm(): void {
    const formTitle = document.querySelector('#add-grob-form h4') as HTMLElement;
    const submitBtn = document.getElementById('grob-form-submit-btn') as HTMLButtonElement;
    const cancelBtn = document.getElementById('grob-form-cancel-btn') as HTMLButtonElement;
    const nameInput = document.getElementById('grob-name') as HTMLInputElement;
    const apiKeyInput = document.getElementById('grob-api-key') as HTMLInputElement;
    const editNameInput = document.getElementById('edit-grob-name') as HTMLInputElement;

    if (!formTitle || !submitBtn || !cancelBtn || !nameInput) return;

    // 恢复添加模式
    formTitle.textContent = '添加 API';
    submitBtn.textContent = '添加';
    cancelBtn.style.display = 'none';

    // 重置字段
    nameInput.value = '';
    nameInput.disabled = false;
    apiKeyInput.required = true; // 恢复必填验证

    // 移除隐藏字段
    if (editNameInput) editNameInput.remove();

    // 重置表单
    const form = document.getElementById('add-grob-form') as HTMLFormElement;
    form?.reset();
  }

  /**
   * 处理重启 Grob
   */
  private async handleRestartGrob(): Promise<void> {
    if (!confirm('确定要重启 Grob 服务吗？')) return;

    try {
      await this.apiClient.restartGrob();
      this.showNotification('Grob 服务正在重启...');
      // 延迟刷新状态
      setTimeout(() => this.loadGrob(), 3000);
    } catch (error) {
      console.error('Failed to restart Grob:', error);
      alert(error instanceof Error ? error.message : '重启失败');
    }
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', () => this.hide());
    }

    if (this.addKeyForm) {
      this.addKeyForm.removeEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    if (this.keysList) {
      this.keysList.removeEventListener('click', (e) => this.handleKeysListClick(e));
    }
  }
}
